import {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import ListSubheader from "@mui/material/ListSubheader";
import {
  autocompleteClasses,
  AutocompleteRenderInputParams,
  Autocomplete as MUIAutoComplete,
  Popper,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FieldError from "../fieldError";
import { ListChildComponentProps, VariableSizeList } from "react-window";

const LISTBOX_PADDING = 4; // px

// A value wider than the box scrolls into view while hovered, then back.
const scrollToEnd = (event: React.MouseEvent<HTMLInputElement>) => {
  const input = event.currentTarget;
  if (input.scrollWidth > input.clientWidth) {
    input.scrollTo({ left: input.scrollWidth, behavior: "smooth" });
  }
};

const scrollToStart = (event: React.MouseEvent<HTMLInputElement>) =>
  event.currentTarget.scrollTo({ left: 0, behavior: "smooth" });
type OptionTextAlign = "left" | "center" | "right";

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;

  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];
  const fontSize: number = (dataSet as any)[3] ?? 14;
  const textAlign: OptionTextAlign = (dataSet as any)[4] ?? "left";

  return (
    <Typography
      key={key}
      component="li"
      {...optionProps}
      noWrap
      style={{ ...inlineStyle, fontSize, textAlign, width: "100%" }}
    >
      {`${dataSet[1].label}`}
    </Typography>
  );
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: ReactElement<unknown>[] = [];

  (children as ReactElement<unknown>[]).forEach(
    (
      item: ReactElement<unknown> & {
        children?: ReactElement<unknown>[];
      },
    ) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    },
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: ReactElement<unknown>) => {
    if (child.hasOwnProperty("group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

export interface InputProps {
  options: { id: number; label: string }[];
  label?: string;
  onChange: (value: { id: number; label: string } | null) => void;
  name?: string;
  value?: number | null;
  disabled?: boolean;
  virtualized?: boolean;
  size?: "tiny" | "small" | "medium";
  textAlign?: "left" | "center" | "right";
  disableClearable?: boolean;
  noBorder?: boolean;
  optionFontSize?: number;
  /** Validation message rendered beneath the field. */
  error?: string;
}

const AutoComplete: React.FC<InputProps> = ({
  options,
  onChange,
  value,
  name,
  label = "",
  disabled = false,
  virtualized = false,
  size = "small",
  textAlign = "left",
  disableClearable = true,
  noBorder = false,
  optionFontSize = 14,
  error,
}) => {
  const selectedValue = useMemo(
    () => options.find(({ id }) => id === value),
    [options, value],
  );

  const muiSize = size === "tiny" ? "small" : size;
  const centerAlignLeftInset = textAlign === "center" ? "28px" : undefined;
  const autoCompleteSx = {
    "& .MuiInputBase-input": {
      ...(size === "tiny"
        ? {
            // iOS Safari zooms the page when a focused input renders under
            // 16px, so the tiny size only applies from sm up.
            fontSize: { xs: 16, sm: 15 },
            height: { xs: 16, sm: 15 },
            padding: 1,
          }
        : {}),
      textAlign,
    },
    ...(size === "tiny"
      ? {
          "& .MuiFormLabel-root": {
            marginTop: value ? -0.8 : -1.5,
            fontSize: 15,
            height: 15,
            padding: 1,
          },
        }
      : {}),
    ...(noBorder
      ? {
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              border: "none",
            },
        }
      : {}),
  };

  const renderTextField = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      name={name}
      label={label}
      variant="outlined"
      inputProps={{
        ...params.inputProps,
        title: selectedValue?.label,
        onMouseEnter: scrollToEnd,
        onMouseLeave: scrollToStart,
        style: {
          ...(params.inputProps.style || {}),
          textAlign,
          paddingLeft: centerAlignLeftInset,
        },
      }}
    />
  );

  if (!virtualized) {
    return (
      <div>
        <MUIAutoComplete
          options={options}
          value={selectedValue ?? undefined}
          disableClearable={disableClearable}
          renderInput={renderTextField}
          renderOption={(props, option) => {
            const { key, ...rest } = props as any;
            return (
              <li
                key={key}
                {...rest}
                style={{ fontSize: optionFontSize, textAlign, width: "100%" }}
              >
                {option.label}
              </li>
            );
          }}
          onChange={(_, value) => onChange(value)}
          fullWidth
          disabled={disabled}
          size={muiSize}
          sx={autoCompleteSx}
        />

        <FieldError error={error} />
      </div>
    );
  }

  return (
    <div>
      <MUIAutoComplete
        options={options}
        disableListWrap
        disableClearable={disableClearable}
        value={selectedValue ?? undefined}
        renderInput={renderTextField}
        renderOption={(props, option, state) =>
          [props, option, state.index, optionFontSize, textAlign] as ReactNode
        }
        // groupBy={(option) => option.label.slice(0, 4).toUpperCase()}
        // renderGroup={(params) => params as any}
        onChange={(_, value) => onChange(value)}
        fullWidth
        disabled={disabled}
        slots={{
          popper: StyledPopper,
        }}
        slotProps={{
          listbox: {
            component: ListboxComponent,
          },
        }}
        size={muiSize}
        sx={autoCompleteSx}
      />

      <FieldError error={error} />
    </div>
  );
};

export default AutoComplete;
